import { mrzLines } from "./mrz";
import { parseSignature, readCardText } from "./signature";
import { csvRows, fileKind, linesFromRows, rowsAsText, xlsxRows } from "./priceList";
import { stripMessage } from "./stripMessage";
import { FleetItem, fleetFromRows } from "./fleetList";
import type { ProposedPriceLine } from "./priceList";
import { DocumentType } from "../types/Lookups";

/**
 * "Attach" in Ask: work out what a file is, by CODE first, on this computer.
 * Spreadsheets with a price column are price lists; vCards, and photos that
 * read like a business card, are contacts (names never go to the AI — D18);
 * a photo with a passport/ID machine-readable zone never leaves the device.
 * Text and other spreadsheets become text for Ask (signatures and quoted
 * replies stripped, D18). Only photos and PDFs the code can't place go to
 * the AI reader (/ai/attachments/read, D12).
 */

export type AttachmentKind =
    | "price_list" | "fleet_list" | "booking" | "trip_details" | "contact_card" | "receipt" | "supplier_invoice"
    | "identity_document" | "record_document" | "other";

/** What the browser decided; "server" = send it to the AI reader. */
export type LocalVerdict =
    | { kind: "contact_card"; text: string }
    | { kind: "identity_document" }
    | { kind: "price_list" }
    | { kind: "fleet_list" }
    | { kind: "text"; text: string }
    | { kind: "server"; text?: string }
    | { kind: "unsupported"; reason: string };

/** The AI reader's answer (same shape as the server's AttachmentRead). */
export interface AttachmentRead {
    kind: AttachmentKind;
    title: string | null;
    documentType: string | null;
    validFrom: string | null;
    validTo: string | null;
    missionReference: string | null;
    trip: unknown | null;
    details: string | null;
    /** A price list's lines, read in the same AI read (place as written). */
    priceLines?: ProposedPriceLine[];
    /** A fleet list's items, read in the same AI read. */
    fleetItems?: FleetItem[];
}

export const ATTACH_ACCEPT = "image/*,application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xlsx,.csv,.txt,.eml,.vcf,text/plain,text/csv,text/vcard";
export const MAX_ATTACH_BYTES = 5 * 1024 * 1024;

/** What the person's words say the file is, if they say. */
export const kindFromWords = (words: string): AttachmentKind | null => {
    const text = words.toLowerCase();
    if (/\b(price|rate)\s*(lists?|sheets?|cards?)\b|\btariffs?\b|قائمة أسعار/.test(text)) return "price_list";
    if (/\b(business\s*card|contact|signature|vcard)\b|بطاقة/.test(text)) return "contact_card";
    if (/\b(e-?tickets?|booking|flights?|hotel|itinerary|boarding pass)\b|حجز/.test(text)) return "booking";
    if (/\breceipts?\b|إيصال/.test(text)) return "receipt";
    if (/\b(supplier|vendor)\s+invoices?\b/.test(text)) return "supplier_invoice";
    return null;
};

/** A mission reference in the person's words. */
export const missionRefFromWords = (words: string): string | null => {
    const match = /\bMSN-\d{1,6}\b/i.exec(words)
        // any reference after the word mission ("mission MIS-APP-REVIEW-DEMO")
        ?? /\bmission\s+(?:ref(?:erence)?\.?\s*)?([A-Z0-9]+(?:-[A-Z0-9]+)+)\b/i.exec(words)
        // a reference typed on its own ("MIS-APP-REVIEW-DEMO")
        ?? /^\s*([A-Z]{2,5}-[A-Z0-9]+(?:-[A-Z0-9]+)*)\s*$/i.exec(words);
    return match ? (match[1] ?? match[0]).toUpperCase() : null;
};

/**
 * The record a person names in a short reply ("yes the provider name is ABC",
 * "it's Gulf Shield", "add this to App Review Demo Provider", or just "ABC").
 * @returns The name, or null when the words don't name one.
 */
export const nameFromWords = (words: string): string | null => {
    // a question never names the record ("who is on shift tonight?")
    if (/\?\s*$/.test(String(words ?? ""))) return null;
    const text = String(words ?? "").trim().replace(/[.!]+$/, "");
    // "create new provider ABC and attach this price sheet to it"
    const created = /\b(?:create|add|make|open|set\s+up|start|new)\s+(?:a\s+|an\s+)?(?:new\s+)?(?:provider|supplier|vendor|client|company)\s+(?:called\s+|named\s+|names?\s+|name\s*(?:is\s+|:\s*))?["“']?([^"”',.;]{2,60}?)["”']?(?=\s+(?:and|with|then|to|for|from)\b|[,.;]|$)/i.exec(text);
    if (created) return created[1].replace(/^(?:the|a|an)\s+/i, "").trim() || null;
    const named = /\b(?:name\s+is|is\s+called|called|named|(?:provider|supplier|vendor|client|company)\s+is)\s+["“']?([^"”'.,!?]{2,60}?)["”']?$/i.exec(text)
        ?? /\b(?:it'?s|its|for|from|of|to)\s+["“']?([^"”'.,!?]{2,60}?)["”']?(?:'s)?$/i.exec(text);
    const name = (named?.[1] ?? (text.split(/\s+/).length <= 4 ? text : "")).replace(/^(?:the|a|an|yes|yeah|ok)\b[\s,]*/i, "").trim();
    return name.length >= 2 && !/^(it|this|that|them|him|her|yes|no)$/i.test(name) ? name : null;
};

/** The person asks for a new record ("create new provider ABC", "add a new supplier"). */
export const wantsNewRecord = (words: string): boolean =>
    /\b(?:create|add|make|open|set\s+up|start)\b[^.?!]{0,20}\b(?:new\s+)?(?:provider|supplier|vendor|client|company)\b|\bnew\s+(?:provider|supplier|vendor|client|company)\b/i.test(String(words ?? ""));

/** Little words that make a line a sentence ("please arrange the pickup for us"). */
const SENTENCE_WORDS = /^(?:the|a|an|and|to|of|for|please|we|our|you|your|is|are|was|be|will|with|in|on|at|this|that|it|can|could|would|kindly|i|me|my)$/i;

/** A company document's labelled line (OCR may clip the label: "Legal for", "Tax numbe."). */
const DOCUMENT_LABEL = /^\s*(?:trade\s+name|legal\s+(?:name|for)|company\s+name|licen[cs]ed?|licen[cs]e\s+(?:no|number)|registr(?:y|ation)|commercial\s+reg|tax\s+(?:reg|num|no)|trn\b|activities|acti\b|expiry|issue\s+date|date\s+of\s+issue)/i;

/** Short text with an email or a phone and few lines: a card or a signature. */
export const looksLikeCard = (text: string): boolean => {
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (!lines.length || lines.length > 14 || text.length > 700) return false;
    // a card has short lines; a sentence (real words that read like one) means it's a message with a signature —
    // a card's long address line or a dashed heading ("ARMOURED VEHICLES - RIYADH - DAMMAM") isn't one
    const isSentence = (line: string) => {
        const words = line.split(/\s+/).filter((word) => /[\p{L}]{2,}/u.test(word));
        return words.length >= 8 && words.filter((word) => SENTENCE_WORDS.test(word)).length >= 2;
    };
    if (lines.some(isSentence)) return false;
    // a licence or registration has labelled lines (trade name, legal form, tax number …): a document, not a card
    if (lines.filter((line) => DOCUMENT_LABEL.test(line)).length >= 2) return false;
    const parsed = parseSignature(text);
    return parsed.emails.length > 0 || parsed.phones.length > 0;
};

/** A passport or ID's machine-readable zone in OCR text. */
export const hasMrz = (text: string): boolean => mrzLines(text).length >= 2;

const readText = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
});
const readBuffer = (file: File): Promise<ArrayBuffer> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
});

const NO_OPTIONS = { services: [], places: [], resources: [] };

/**
 * Decide on this computer what the file is.
 * @param file - The attached file.
 * @param words - What the person typed with it.
 * @param ocr - Reads a photo's text (injected for tests; tesseract.js by default).
 */
export const classifyLocally = async (file: File, words: string, ocr: (file: File) => Promise<string> = readCardText): Promise<LocalVerdict> => {
    if (file.size > MAX_ATTACH_BYTES) return { kind: "unsupported", reason: "The file is over 5 MB." };
    const name = file.name.toLowerCase();
    const kind = fileKind(file);
    const said = kindFromWords(words);
    if (name.endsWith(".vcf") || file.type === "text/vcard") return { kind: "contact_card", text: await readText(file) };
    if (kind === "old-excel") return { kind: "unsupported", reason: "Old .xls files can't be read — save it as .xlsx or CSV." };
    if (kind === "sheet" || kind === "csv") {
        const rows = kind === "sheet" ? await xlsxRows(await readBuffer(file), 200) : csvRows(await readText(file)).slice(0, 200);
        if (said === "price_list" || linesFromRows(rows, NO_OPTIONS) !== null) return { kind: "price_list" };
        if (fleetFromRows(rows)) return { kind: "fleet_list" };
        return { kind: "text", text: rowsAsText(rows.slice(0, 80), 3500) };
    }
    if (name.endsWith(".txt") || name.endsWith(".eml") || file.type.startsWith("text/")) {
        const text = await readText(file);
        if (said === "contact_card" || looksLikeCard(text)) return { kind: "contact_card", text };
        return { kind: "text", text: stripMessage(text, 3500) };
    }
    if (kind === "image") {
        // one read on this computer: a passport never leaves it, a card is read here
        let text = "";
        try {
            text = await ocr(file);
        } catch {
            text = "";
        }
        if (hasMrz(text.toUpperCase().replace(/\s+(?=<)/g, ""))) return { kind: "identity_document" };
        if (said === "contact_card" || (said === null && looksLikeCard(text))) return { kind: "contact_card", text };
        // what OCR read stays on this computer: it names the company the document belongs to
        return { kind: "server", text };
    }
    if (kind === "pdf" || name.endsWith(".docx")) return { kind: "server" };
    return { kind: "unsupported", reason: "Attach a photo, PDF, Word (.docx), Excel (.xlsx), CSV or text file." };
};

/** The reader's document type → CRX's document type. */
export const crxDocumentType = (type: string | null): DocumentType => {
    switch (type) {
        case "contract": return DocumentType.Contract;
        case "certificate": return DocumentType.Certificate;
        case "license": case "permit": return DocumentType.License;
        case "insurance": return DocumentType.Insurance;
        case "visa": return DocumentType.Visa;
        case "policy": return DocumentType.Policy;
        case "nda": return DocumentType.NDA;
        case "company_profile": return DocumentType.CompanyProfile;
        default: return DocumentType.Additional;
    }
};

/** A new, unsaved document row for a record's documents list. */
export const documentRow = (file: File, read: Pick<AttachmentRead, "title" | "documentType" | "validFrom" | "validTo">) => ({
    documentId: null,
    name: read.title || file.name.replace(/\.[^.]+$/, ""),
    attachment: "",
    document: file,
    type: crxDocumentType(read.documentType),
    dateFrom: read.validFrom,
    dateTo: read.validTo,
    isValid: true,
    deletedAt: null,
});
